import { createSelector } from 'reselect';
import spacetime from 'spacetime';

import { KnmErknmStatusCodesEnum } from 'APIPackage/CatalogsModule/enums/KnmErknmStatusCodesEnum';
import { KnmErpStatusCodesEnum } from 'APIPackage/CatalogsModule/enums/KnmErpStatusCodesEnum';
import { KnmTypeCodesEnum } from 'APIPackage/CatalogsModule/enums/KnmTypeCodesEnum';
import { KnmEditStatusesEnum } from 'APIPackage/KnmModule/enums/KnmEditStatusesEnum';
import {
  getIsPlanErknmStatusConsidered,
  getIsPlanErknmStatusUnderConsideration,
} from 'APIPackage/PlanListModule/common/getPlanErknmStatusFlags';
import { ErknmPlanStatusesEnum } from 'APIPackage/PlanListModule/enums/ErknmPlanStatusesEnum';
import {
  getUserProfileIsTest,
  getUserRoleIsAdmin,
  getUserRoleIsOmbudsman,
  getUserRoleIsProsecutorOrSuperProsecutor,
  getUserRoleIsSupervisor,
} from 'AuthPackage/MainModule/selectors/authSelectors';
import {
  makeGetAccountChecklistTemplatesItemSelectOptions,
  makeGetAccountKnoOrganizationFunctionsGroupSelectOptions,
  makeGetAccountRequirementTemplatesItemSelectOptions,
} from 'CorePackage/AccountModule/selectors/accountSelectors';
import {
  getCatalogErknmKnmReasonTypesSelectOptionsSelector,
  getCatalogKnmTypes248SelectOptionsSelector,
  getCatalogKnmTypes294SelectOptions,
  makeGetCatalogErknmKnmRejectReasonTypesSelectOptions,
  makeGetCatalogKnmConditionsSelectOptions,
  makeGetCatalogKnmErpConditionsSelectOptions,
  makeGetCatalogKnmErpStatusesSelectOptions,
  makeGetCatalogKnmRejectReasonTypesSelectOptions,
  makeGetCatalogKnmSupervisionTypesExtendedSelectOptions,
  makeGetCatalogPmConditionsSelectOptions,
} from 'CorePackage/CatalogsModule/selectors/catalogsSelectors';
import {
  getDictionariesEventUuidData,
  getDictionariesItemValueData,
  getDictionariesKnmKindsSelectOptionsSelector,
  getDictionariesKnmTypesData,
  getDictionariesKnoSupervisionStatus,
  getDictionariesKnoSupervisionUuidSelector,
  getDictionariesSupervisionTypesSelectOptionsSelector,
  makeGetDictionariesAcceptedDocumentsSelectOptions,
  makeGetDictionariesChecklistsSelectOptions,
  makeGetDictionariesDangerClassesSelectOptions,
  makeGetDictionariesEventTypesSelectOptions,
  makeGetDictionariesParticipantPositionsSelectOptions,
  makeGetDictionariesPermitDocsSelectOptions,
  makeGetDictionariesPositionsSelectOptions,
  makeGetDictionariesProfKindsSelectOptions,
  makeGetDictionariesRequirementsSelectOptions,
  makeGetDictionariesRiskCategoriesSelectOptions,
  makeGetDictionariesRiskIndicatorsSelectOptions,
  makeGetDictionariesSubjectKindsSelectOptions,
  makeGetDictionariesSubjectSubKindsSelectOptions,
  makeGetDictionariesSubjectTypesSelectOptions,
  makeGetDictionariesTargetEntityFounderSelectOptions,
  makeGetDictionariesTargetEntitySelectOptions,
  makeGetErvkChecklistsSelectOptions,
  makeGetFilteredFederalLawsSelectOptions,
} from 'CorePackage/DictionariesModule/selectors/dictionariesSelectors';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { NEW_KNM_ID } from 'CorePackage/MainModule/routes/routes';
import { makeGetRegistryExportOkvedItemSelectOptions } from 'CorePackage/RegistryModule/selectors/registrySelectors';
import {
  getPlanListPlanErknmStatus,
  getPlanListPlanStartDate,
} from 'PlanListPackage/MainModule/selectors/planListSelectors';
import { IKnmStatusSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnmStatusSelectOption';
import { KnmResultInfoTypeCodesEnum } from 'APIPackage/CatalogsModule/enums/KnmResultInfoTypeCodesEnum';
import { createKnmTypeSelectOption } from 'CorePackage/CatalogsModule/common/createKnmTypeSelectOption';
import { getCoreIsErknm } from 'CorePackage/MainModule/selectors/coreSelectors';

import { convertKnmUrlIdToId } from '../common/convertKnmUrlIdToId';
import { knmChecklistTemplatesGetBasicParams } from '../constants/knmChecklistTemplatesGetBasicParams';
import { knmRequirementTemplatesGetBasicParams } from '../constants/knmRequirementTemplatesGetBasicParams';
import { KnmLoadingFlagsEnum } from '../enums/KnmLoadingFlagsEnum';
import { KnmStatusesEnum } from '../enums/KnmStatusesEnum';
import { KnmSystemTypesEnum } from '../enums/KnmSystemTypesEnum';
import { KnmModalsEnum } from '../enums/KnmModalsEnum';
import { parseDate } from '../../../CorePackage/MainModule/common/formatDate';
import { DATE_FORMAT_LONG_FNS, SYSTEM_DATE_FORMAT_FNS } from '../../../CorePackage/MainModule/constants/dateFormats';
import { tzMoscow } from '../../../CorePackage/MainModule/constants/timeZone';
import { ApprovedTypesEnum } from '../../../APIPackage/KnmModule/enums/ApprovedTypesEnum';
import { DictionaryUUIDsEnum } from '../../../CorePackage/DictionariesModule/enums/DictionaryUUIDsEnum';
import { getAdminFetchSettings } from '../../../PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { isLaterThanAdminSetting } from '../../../CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { AdminSettingCodesEnum } from '../../../APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { IRegistryExportOkvedFilterParams } from '../../../CorePackage/RegistryModule/interfaces/IRegistryExportOkvedFilterParams';
import { createUserOrganizationWithDistrictSelectOption } from '../../../CorePackage/AccountModule/common/createUserOrganizationWithDistrictSelectOption';
import { getPlanErknmStatusByStatusTitle } from '../../../APIPackage/PlanListModule/common/getPlanErknmStatusByStatusTitle';
import { FEDERAL_LOW_248_GUID } from '../constants/erknmFederalLawUuid';

export const getKnm = (state: IRootState) => state.knm;
export const getKnmActionsDropdownVisible = (state: IRootState) => state.knm?.actionsDropdownVisible;
export const getKnmErrorFields = (state: IRootState) => state.knm?.errorFields;
export const getKnmStatusHintText = (state: IRootState) => state.knm.statusHintText;
export const getKnmStatusHintFields = (state: IRootState) => state.knm.statusHintFields;
export const getKnmUrlId = (state: IRootState) => state.knm?.urlId;
export const getKnmId = (state: IRootState) => {
  const urlId = getKnmUrlId(state);

  return convertKnmUrlIdToId(urlId);
};
export const getKnmLoading = (state: IRootState) => {
  const getKnmStatus = state.knm.getKnm?.status;

  if (getKnmStatus) {
    return getKnmStatus === KnmStatusesEnum.LOADING;
  }

  return Boolean(state.knm.knmLoading);
};
export const getKnmData = (state: IRootState) => state.knm?.rawData;
export const getKnmDataAgreementOnApproval = (state: IRootState) => !!getKnmData(state)?.knmErknm?.agreementOnApproval;
export const getPmDataAgreementOnApproval = (state: IRootState) => !!getKnmData(state)?.pmErknm?.agreementOnApproval;
export const getKnmProsecutorOrganization = (state: IRootState) => getKnmData(state)?.prosecutorOrganization;
export const getKnmDataStatus = (state: IRootState) => state.knm?.rawData?.status;
export const getKnmDataId = (state: IRootState) => getKnmData(state)?.id;
export const getKnmDataSigned = (state: IRootState) => getKnmData(state)?.signed;
export const getKnmDataResigningRequired = (state: IRootState) => !!getKnmData(state)?.resigningRequired;
export const getKnmDataLateChangesFields = (state: IRootState) => state.knm?.lateChangesFields;
export const getKnmDataLateChangesFieldLinks = (state: IRootState) => state.knm?.lateChangesFieldLinks;
export const getKnmDataErpId = (state: IRootState) => getKnmData(state)?.erpId;
export const getKnmDataPlanId = (state: IRootState) => getKnmData(state)?.planId;
export const getKnmDataErknmUntimelyBasic = (state: IRootState) => getKnmData(state)?.knmErknm?.untimelyBasic;
export const getKnmDataGuid = (state: IRootState) => getKnmData(state)?.guid;
export const getKnmDataCreationDate = (state: IRootState) => getKnmData(state)?.creationDate;
export const getKnmDataStartDate = (state: IRootState) => getKnmData(state)?.startDate;
export const getKnmDataIsTest = (state: IRootState) => !!getKnmData(state)?.isTest;
export const getKnmDataCheckSubjectResult = (state: IRootState) => getKnmData(state)?.organization?.checkSubjectResult;
export const getKnmDataIsIndividualEntrepreneur = (state: IRootState) =>
  getKnmData(state)?.organization?.isIndividualEntrepreneur;
export const getKnmDataIsLegalEntity = (state: IRootState) => getKnmData(state)?.organization?.isLegalEntity;
export const getKnmDataDeleted = (state: IRootState) => getKnmData(state)?.deleted;
export const getKnmDataCreatedBy = (state: IRootState) => getKnmData(state)?.createdBy;
export const getKnmDataUpdatedBy = (state: IRootState) => getKnmData(state)?.updatedBy;
export const getKnmDataCreated = (state: IRootState) => getKnmData(state)?.created;
export const getKnmDataUpdated = (state: IRootState) => getKnmData(state)?.updated;
export const getKnmDataVersion = (state: IRootState) => getKnmData(state)?.version;
export const getKnmDataNote = (state: IRootState) => getKnmData(state)?.note;
export const getKnmDataErknmObjects = (state: IRootState) => getKnmData(state)?.knmErknm?.objects;
export const getKnmHasReasonsImpossibility = (state: IRootState) =>
  !!(
    getKnmData(state)?.approve?.impossibleConductionReason ||
    getKnmData(state)?.objects?.some(item =>
      item.result?.additionalInfo?.some(
        additional => additional.type.code === KnmResultInfoTypeCodesEnum.UNIMPOSSIBLE_REASON_I,
      ),
    )
  );
export const getKnmDataErknmApproveApproved = (state: IRootState) => getKnmData(state)?.knmErknm?.approve?.approved;
export const getKnmDataErknmApproveAppeal = (state: IRootState) => getKnmData(state)?.knmErknm?.approve?.appeal;
export const getKnmDataErknmApproveRemarks = (state: IRootState) => getKnmData(state)?.knmErknm?.approve?.remarks;
export const getKnmDataErknmWebNotificationInfos = (state: IRootState) =>
  getKnmData(state)?.knmErknm?.webNotificationInfos;
export const getKnmDataErknmIsPlanned = (state: IRootState) => getKnmData(state)?.type?.code === KnmTypeCodesEnum.PP;
export const getKnmDataMassCancel = (state: IRootState) => getKnmData(state)?.massCancel;
export const getKnmDataMassDelete = (state: IRootState) => getKnmData(state)?.massDelete;
export const getKnmIsMassCancelBlock = (state: IRootState) => !!getKnmData(state)?.massCancel?.isBlockEditing;
export const getKnmGet = (state: IRootState) => state.knm?.getKnm;
export const getKnmGetStatus = (state: IRootState) => getKnmGet(state)?.status;
export const getKnmUpdate = (state: IRootState) => state.knm?.updateKnm;
export const getKnmUpdateStatus = (state: IRootState) => getKnmUpdate(state)?.status;
export const getKnmUpdateIsSuccess = (state: IRootState) => {
  const status: KnmStatusesEnum | undefined = getKnmUpdateStatus(state);

  return status === KnmStatusesEnum.SUCCEEDED;
};
export const getKnmCopy = (state: IRootState) => state.knm?.copyKnm;
export const getKnmCopyStatus = (state: IRootState) => getKnmCopy(state)?.status;
export const getKnmExcludeFromPlan = (state: IRootState) => state.knm?.excludeKnmFromPlan;
export const getKnmExcludeFromPlanStatus = (state: IRootState) => getKnmExcludeFromPlan(state)?.status;
export const getKnmInitialData = (state: IRootState) => state.knm?.initialData;
export const getKnmExportKnm = (state: IRootState) => state.knm?.exportKnm;
export const getKnmFormRef = (state: IRootState) => state.knm?.formRef;
export const getKnmFormAgreementsValue = (state: IRootState) => {
  const formRef = getKnmFormRef(state);

  return formRef?.current?.getFieldState?.('agreements')?.value;
};
export const getKnmErknmKnoOrganizationFormValue = (state: IRootState) => state.knm?.formValues?.erknmKnoOrganization;
export const getKnmErknmFormValues = (state: IRootState) => state.knm?.formValues;
export const getKnmErknmKnoOrganizationDictVersionIdFormValue = (state: IRootState) =>
  getKnmErknmKnoOrganizationFormValue(state)?.value.dictVersionId;
export const getKnmErknmSupervisionTypeFormValue = (state: IRootState) => state.knm?.formValues?.erknmSupervisionType;
export const getKnmErvkSupervisionTypeFormValue = (state: IRootState) => state.knm?.formValues?.supervisionTypeErvk;
export const getKnmErknmKnmKindVersionIdFormValue = (state: IRootState) => state.knm?.formValues?.erknmKnmKindVersionId;
export const getKnmErknmSupervisionTypeDictVersionIdFormValue = (state: IRootState) =>
  getKnmErknmSupervisionTypeFormValue(state)?.value.dictVersionId;
export const getKnmErvkSupervisionTypeDictVersionIdFormValue = (state: IRootState) =>
  getKnmErvkSupervisionTypeFormValue(state)?.value.dictVersionId;
export const getKnmErvkKnmKindDictVersionIdFormValue = (state: IRootState) =>
  state.knm?.formValues?.kindErvk?.value.dictVersionId;
export const getKnmErknmKnmKindDictVersionIdFormValue = (state: IRootState) =>
  state.knm?.formValues?.erknmKnmKindVersionId;
export const getKnmKnoOrganizationFormValue = (state: IRootState) => state.knm?.formValues?.knoOrganization;
export const getKnmKnoOrganizationIdFormValue = (state: IRootState) => state.knm?.formValues?.knoOrganization?.id;
export const getKnmErpOrganizationFormValue = (state: IRootState) => state.knm.formValues?.knoOrganizationErvk;
export const getKnmErpOrganizationCodeFormValue = (state: IRootState) =>
  state.knm.formValues?.knoOrganizationErvk?.code;
export const getKnmErpKnoOrganizationDictVersionIdFormValue = (state: IRootState) =>
  getKnmErpOrganizationFormValue(state)?.value.dictVersionId;
export const getKnmErpSupervisionTypeFormValue = (state: IRootState) => state.knm.formValues?.supervisionTypeErvk;
export const getKnmErpSupervisionTypeDictVersionIdFormValue = (state: IRootState) =>
  getKnmErpSupervisionTypeFormValue(state)?.value.dictVersionId;
export const getKnmErpFederalLawFormValue = (state: IRootState) => state.knm.formValues?.federalLaw;
export const getKnmErpFederalLawDictVersionIdFormValue = (state: IRootState) =>
  getKnmErpFederalLawFormValue(state)?.value.dictVersionId;
export const getKnmLegalEntityData = (state: IRootState) => state.knm?.legalEntityData;
export const getKnmLoadingFlags = (state: IRootState) => state.knm?.loadingFlags;
export const getKnmLoadingFlag = (state: IRootState, name: KnmLoadingFlagsEnum) => state.knm?.loadingFlags?.[name];
export const getKnmOnSaveButtonClick = (state: IRootState) => state.knm?.onSaveButtonClick;
export const getKnmOnAdminSaveButtonClick = (state: IRootState) => state.knm?.onAdminSaveButtonClick;
export const getKnmOpenChangeKnmModal = (state: IRootState) => state.knm?.openChangeKnmModal;
export const getKnmOpenRemoveFromPlanModal = (state: IRootState) => state.knm?.openRemoveFromPlanModal;
export const getKnmPath = (state: IRootState) => state.knm?.path;
export const getKnmPlanFind = (state: IRootState) => state.knm?.planFind;
export const getKnmPlanFindData = (state: IRootState) => state.knm?.planFind?.data;
export const getKnmPlanFindDataYear = (state: IRootState) => state.knm?.planFind?.data?.year;
export const getKnmPlanFindDataStatus = (state: IRootState) => {
  const planErknmStatusTitle = getKnmPlanFindData(state)?.erknmStatus;

  return getPlanErknmStatusByStatusTitle(planErknmStatusTitle);
};
export const getKnmPlanFindStatus = (state: IRootState) => state.knm?.planFind?.status;
export const getKnmViolations = (state: IRootState) => state.knm?.violations;
export const getKnmViolationsData = (state: IRootState) => state.knm?.violations?.data;
export const getKnmViolationsStatus = (state: IRootState) => state.knm?.violations?.status;
export const getKnmModalIsVisible = (modalNames: KnmModalsEnum[]) => (state: IRootState) =>
  modalNames.some(modalName => state.knm?.knmVisibleModals?.includes(modalName));
export const getKnmModalParams = (state: IRootState) => state.knm?.modalParams;
export const getKnmPublishKnm = (state: IRootState) => state.knm?.publishKnm;
export const getKnmEndByErknm = (state: IRootState) => state.knm?.knmEndByErknm;
export const getKnmUnpublishKnm = (state: IRootState) => state.knm?.unpublishKnm;
export const getKnmPublishedStatus = (state: IRootState) => state.knm?.publishedStatus;
export const getKnmStatus = (state: IRootState) => state.knm?.status;
export const getKnmStatusCode = (state: IRootState) => getKnmStatus(state)?.code;
export const getKnmIsErpStatusNew = (state: IRootState) => getKnmStatusCode(state) === KnmErpStatusCodesEnum.TYPE_NEW;
export const getNpaData = (state: IRootState) => state.knm.npaData;
export const getPunishmentNature = (state: IRootState) => state.knm.punishmentNature;
export const getKnmIsErpStatusClosedErknm = (state: IRootState) =>
  getKnmStatusCode(state) === KnmErpStatusCodesEnum.TYPE_ERKNM_CLOSED;
export const getKnmIsErpStatusDeleted = (state: IRootState) =>
  getKnmStatusCode(state) === KnmErpStatusCodesEnum.TYPE_DELETED;
export const getKnmIsErpStatusDisapproved = (state: IRootState) =>
  getKnmStatusCode(state) === KnmErpStatusCodesEnum.TYPE_DISAPPROVED;
export const getKnmIsErpStatusTypeWaitingCarryOut = (state: IRootState) =>
  getKnmStatusCode(state) === KnmErpStatusCodesEnum.TYPE_WAITING_CARRY_OUT;
export const getKnmIsErpStatusSyspApprove = (state: IRootState) =>
  getKnmStatusCode(state) === KnmErpStatusCodesEnum.TYPE_SYSP_APPROVE;
export const getKnmIsErpStatusTypeCarriedOut = (state: IRootState) =>
  getKnmStatusCode(state) === KnmErpStatusCodesEnum.TYPE_CARRIED_OUT;
export const getKnmIsErpStatusNeedDoOverAgain = (state: IRootState) =>
  getKnmStatusCode(state) === KnmErpStatusCodesEnum.TYPE_NEED_DO_OVER_AGAIN;
export const getKnmIsErknmStatusFillInProcess = (state: IRootState) =>
  getKnmStatusCode(state) === KnmErknmStatusCodesEnum.FILL_IN_PROCESS;
export const getKnmIsErknmStatusReadyForApprove = (state: IRootState) =>
  getKnmStatusCode(state) === KnmErknmStatusCodesEnum.READY_FOR_APPROVE;
export const getKnmIsErknmStatusApproving = (state: IRootState) =>
  getKnmStatusCode(state) === KnmErknmStatusCodesEnum.APPROVING;
export const getKnmIsErknmStatusNotApprove = (state: IRootState) =>
  getKnmStatusCode(state) === KnmErknmStatusCodesEnum.NOT_APPROVE;
export const getKnmIsErknmStatusAppealed = (state: IRootState) =>
  getKnmStatusCode(state) === KnmErknmStatusCodesEnum.APPEALED;
export const getKnmIsErknmStatusRejected = (state: IRootState) =>
  getKnmStatusCode(state) === KnmErknmStatusCodesEnum.REJECTED;
export const getKnmIsErknmStatusCommentsExist = (state: IRootState) =>
  getKnmStatusCode(state) === KnmErknmStatusCodesEnum.COMMENTS_EXIST;
export const getKnmIsErknmStatusRejectAppealed = (state: IRootState) =>
  getKnmStatusCode(state) === KnmErknmStatusCodesEnum.REJECT_APPEALED;
export const getKnmIsErknmStatusDecisionAppealed = (state: IRootState) =>
  getKnmStatusCode(state) === KnmErknmStatusCodesEnum.DECISION_APPEALED;
export const getKnmIsErknmStatusTypeWaitingCarryOut = (state: IRootState) =>
  getKnmStatusCode(state) === KnmErknmStatusCodesEnum.TYPE_WAITING_CARRY_OUT;
export const getKnmIsErknmStatusTypeCarriedOut = (state: IRootState) =>
  getKnmStatusCode(state) === KnmErknmStatusCodesEnum.TYPE_CARRIED_OUT;
export const getKnmIsErknmStatusTypeCompleted = (state: IRootState) =>
  getKnmStatusCode(state) === KnmErknmStatusCodesEnum.TYPE_COMPLETED;
export const getKnmIsErknmStatusTypeDisapproved = (state: IRootState) =>
  getKnmStatusCode(state) === KnmErknmStatusCodesEnum.TYPE_DISAPPROVED;
export const getKnmIsErknmStatusTypeCantBeCarryOut = (state: IRootState) =>
  getKnmStatusCode(state) === KnmErknmStatusCodesEnum.TYPE_CANT_BE_CARRY_OUT;
export const getKnmIsErknmStatusTypeCanceled = (state: IRootState) =>
  getKnmStatusCode(state) === KnmErknmStatusCodesEnum.TYPE_CANCELED;
export const getKnmIsErknmStatusTypeRejected = (state: IRootState) =>
  getKnmStatusCode(state) === KnmErknmStatusCodesEnum.REJECTED;
export const getKnmIsErknmStatusRemark = (state: IRootState) =>
  getKnmStatusCode(state) === KnmErknmStatusCodesEnum.REMARK;
export const getKnmIsChangeSubjectTypeFromActionsAvailable = (state: IRootState) =>
  !!state.knm.isChangeSubjectTypeFromActionsAvailable;
export const getSubjectsTypeChangeOptionAvailable = (state: IRootState) =>
  !!state.knm.rawData?.knmErknm?.subjectsTypeChangeOptionAvailable ||
  !!state.knm.rawData?.pmErknm?.subjectsTypeChangeOptionAvailable;
export const getKnmIsErknmStatusDeleted = (state: IRootState) =>
  getKnmStatusCode(state) === KnmErknmStatusCodesEnum.TYPE_DELETED;
export const getKnmWatchOnlyByTestUser = (state: IRootState) => {
  const isUserTest: boolean = getUserProfileIsTest(state);
  const isTest = getKnmDataIsTest(state);
  const isNewKnm: boolean = getKnmIsNew(state) || false;

  if (isNewKnm) return false;

  return isUserTest ? !isTest : false;
};
export const getKnmIsBasicWatchOnly = (state: IRootState) => {
  const isOmbudsman: boolean | undefined = getUserRoleIsOmbudsman(state);
  const isKnmDeleted: boolean | undefined = getKnmDataDeleted(state);
  const isKnmCanceled: boolean = getKnmIsErknmStatusTypeCanceled(state);
  const isKnmClosedErknm: boolean | undefined = getKnmIsErpStatusClosedErknm(state);
  const isWatchOnlyByTestUser = getKnmWatchOnlyByTestUser(state);
  const isKnmMassCancelBlock = getKnmIsMassCancelBlock(state);

  return (
    isKnmDeleted || isOmbudsman || isKnmCanceled || isKnmClosedErknm || isWatchOnlyByTestUser || isKnmMassCancelBlock
  );
};
export const getKnmIsBasicWatchOnlyWithoutCanceledFlag = (state: IRootState) => {
  const isOmbudsman: boolean | undefined = getUserRoleIsOmbudsman(state);
  const isKnmDeleted: boolean | undefined = getKnmDataDeleted(state);
  const isKnmClosedErknm: boolean | undefined = getKnmIsErpStatusClosedErknm(state);
  const isWatchOnlyByTestUser = getKnmWatchOnlyByTestUser(state);

  return isKnmDeleted || isOmbudsman || isKnmClosedErknm || isWatchOnlyByTestUser;
};
export const getKnmIsBasicWatchOnlyWithoutTestFlag = (state: IRootState) => {
  const isOmbudsman: boolean | undefined = getUserRoleIsOmbudsman(state);
  const isKnmDeleted: boolean | undefined = getKnmDataDeleted(state);
  const isKnmCanceled: boolean = getKnmIsErknmStatusTypeCanceled(state);
  const isKnmClosedErknm: boolean | undefined = getKnmIsErpStatusClosedErknm(state);
  const isKnmMassCancelBlock = getKnmIsMassCancelBlock(state);

  return isKnmDeleted || isOmbudsman || isKnmCanceled || isKnmClosedErknm || isKnmMassCancelBlock;
};

export const getKnmAdminCanEditSubject = (state: IRootState) => {
  const isAdmin: boolean | undefined = getUserRoleIsAdmin(state);
  const isPlanned = !!getKnmDataPlanId(state);

  return isAdmin && !isPlanned;
};

export const getKnmIsCommonWatchOnly = (state: IRootState) => {
  const isBasicWatchOnly: boolean = getKnmIsBasicWatchOnly(state);
  const isKnmEditEnabledByEditors: boolean = getKnmIsEditEnabledByEditors(state);
  const isAdminCanEditSubject: boolean | undefined = getKnmAdminCanEditSubject(state);

  return isBasicWatchOnly || (!isAdminCanEditSubject && !isKnmEditEnabledByEditors);
};
export const getKnmIsCommonWatchOnlyWithoutTestFlag = (state: IRootState) => {
  const isBasicWatchOnly: boolean = getKnmIsBasicWatchOnlyWithoutTestFlag(state);
  const isKnmEditEnabledByEditors: boolean = getKnmIsEditEnabledByEditors(state);

  return isBasicWatchOnly || !isKnmEditEnabledByEditors;
};
const extendWatchOnlyToMultiVersion = (state: IRootState, extendingWatchOnly: boolean) => {
  const isErknm = getKnmSystemType(state) === KnmSystemTypesEnum.ERKNM;
  const isPm = getKnmSystemType(state) === KnmSystemTypesEnum.PM;

  const isKnmStatusTypeCantBeCarryOut: boolean = getKnmIsErknmStatusTypeCantBeCarryOut(state);

  if (isErknm) {
    const isKnmStatusDecisionAppealed: boolean = getKnmIsErknmStatusDecisionAppealed(state);
    const isSupervisor: boolean | undefined = getUserRoleIsSupervisor(state);
    const knmPlanStatus: ErknmPlanStatusesEnum | undefined = getPlanListPlanErknmStatus(state);
    const isKnmPlanStatusUnderConsideration: boolean = getIsPlanErknmStatusUnderConsideration(knmPlanStatus);
    const isAdminCanEditSubject: boolean | undefined = getKnmAdminCanEditSubject(state);

    return (
      extendingWatchOnly ||
      isKnmStatusDecisionAppealed ||
      isKnmStatusTypeCantBeCarryOut ||
      (!isAdminCanEditSubject && isSupervisor && isKnmPlanStatusUnderConsideration)
    );
  }
  if (isPm) {
    return extendingWatchOnly || isKnmStatusTypeCantBeCarryOut;
  }

  return extendingWatchOnly;
};

export const getKnmIsWatchOnly = (state: IRootState) => {
  const isCommonWatchOnly: boolean = getKnmIsCommonWatchOnly(state);

  if (getIsRsnCngPPXXII(state)) {
    return true;
  }

  return extendWatchOnlyToMultiVersion(state, isCommonWatchOnly);
};

export const getKnmErpStatusNew = (state: IRootState) => {
  return !getKnmStatusCode(state) || getKnmIsErpStatusNew(state);
};

// В связи с дополнением соответствующих нормативных правовых актов новыми обязательными требованиями
export const getIsRsnCngPPXXII = (state: IRootState) => {
  const rsnChange = getChangePlanReasonAfterConfirmEvent(state);

  return rsnChange ? rsnChange.some(item => item?.reasonChangeType?.code === 'RSN_CHNG_PP_XXII') : false;
};

// Основание по требованию (указанию) прокурора
export const getIsRsnCngPPXX = (state: IRootState) => {
  const rsnChange = getChangePlanReasonAfterConfirmEvent(state);

  return rsnChange ? rsnChange.some(item => item?.reasonChangeType?.code === 'RSN_CHNG_PP_XX') : false;
};

export const getKnmIsWatchOnlyWithoutTestFlag = (state: IRootState) => {
  const isCommonWatchOnly: boolean = getKnmIsCommonWatchOnlyWithoutTestFlag(state);

  return extendWatchOnlyToMultiVersion(state, isCommonWatchOnly);
};
export const getKnmIsEditorsUpdateEnabled = (state: IRootState) => {
  const isBasicWatchOnly: boolean = getKnmIsBasicWatchOnlyWithoutTestFlag(state);
  const isWatchOnly = extendWatchOnlyToMultiVersion(state, isBasicWatchOnly);
  const isProsecutorOrSuperProsecutor: boolean | undefined = getUserRoleIsProsecutorOrSuperProsecutor(state);
  const isSupervisor: boolean | undefined = getUserRoleIsSupervisor(state);

  return !isWatchOnly && (isProsecutorOrSuperProsecutor || isSupervisor);
};
export const getKnmStatusText = (state: IRootState) => state.knm?.statusText;

export const getKnmKnoSupervisionUuid = (state: IRootState) => {
  const knoOrganizationDictVersionId = getKnmErknmKnoOrganizationDictVersionIdFormValue(state);
  const supervisionTypeDictVersionId = getKnmErknmSupervisionTypeDictVersionIdFormValue(state);

  return getDictionariesKnoSupervisionUuidSelector(state)(knoOrganizationDictVersionId, supervisionTypeDictVersionId);
};

export const getKnmErpKnoSupervisionUuid = (state: IRootState) => {
  const knoOrganizationDictVersionId = getKnmErpKnoOrganizationDictVersionIdFormValue(state);
  const supervisionTypeDictVersionId = getKnmErpSupervisionTypeDictVersionIdFormValue(state);

  return getDictionariesKnoSupervisionUuidSelector(state)(knoOrganizationDictVersionId, supervisionTypeDictVersionId);
};

export const getKnmKnoSupervisionUuidERP = (state: IRootState) => {
  const knoOrganizationDictVersionId = getKnmErpKnoOrganizationDictVersionIdFormValue(state);
  const supervisionTypeDictVersionId = getKnmErpSupervisionTypeDictVersionIdFormValue(state);

  return getDictionariesKnoSupervisionUuidSelector(state)(knoOrganizationDictVersionId, supervisionTypeDictVersionId);
};

export const getDictionaryKnoSupervisionStatus = (state: IRootState) => {
  const knoOrganizationDictVersionId = getKnmErknmKnoOrganizationDictVersionIdFormValue(state);
  const supervisionTypeDictVersionId = getKnmErknmSupervisionTypeDictVersionIdFormValue(state);

  if (!knoOrganizationDictVersionId || !supervisionTypeDictVersionId) {
    return undefined;
  }

  return getDictionariesKnoSupervisionStatus(state, knoOrganizationDictVersionId, supervisionTypeDictVersionId);
};

/**
 * Получение id вида контроля
 */
export const getControlKindUuid = (state: IRootState) => {
  return (
    state?.knmErknm?.initialFormValues?.kindControl?.value?.dictVersionId ||
    state?.knmPm?.initialFormValues?.supervisionTypePm?.value?.dictVersionId
  );
};

export const getKnmIsApproveFieldsEnabled = (state: IRootState) =>
  getKnmDataErknmApproveApproved(state) !== undefined &&
  (getKnmDataErknmApproveApproved(state) === ApprovedTypesEnum.ON_APPROVAL ||
    (getKnmDataErknmApproveApproved(state) === ApprovedTypesEnum.DENIED &&
      getKnmTypeFormValue(state)?.value === KnmTypeCodesEnum.PP) ||
    (getKnmDataErknmApproveApproved(state) === ApprovedTypesEnum.APPROVED &&
      getPlanListPlanErknmStatus(state) === ErknmPlanStatusesEnum.UNDER_CONSIDERATION));

export const getKnmEventUuid = (state: IRootState) => {
  const knoOrganizationDictVersionId = getKnmErknmKnoOrganizationDictVersionIdFormValue(state);
  const supervisionTypeDictVersionId = getKnmErknmSupervisionTypeDictVersionIdFormValue(state);
  const knmKindVersionId = getKnmErknmKnmKindDictVersionIdFormValue(state);

  if (knoOrganizationDictVersionId && supervisionTypeDictVersionId && knmKindVersionId) {
    return getDictionariesEventUuidData(
      state,
      knoOrganizationDictVersionId,
      supervisionTypeDictVersionId,
      knmKindVersionId,
    );
  }

  return undefined;
};
export const getKnmSubjectTypeFormValue = (state: IRootState) => state.knm?.formValues?.subjectType;
export const getKnmSubjectTypeIdFormValue = (state: IRootState) => state.knm?.formValues?.subjectType?.id;
export const getKnmSupervisionTypeFormValue = (state: IRootState) => state.knm?.formValues?.supervisionType;
export const getKnmSupervisionTypeIdFormValue = (state: IRootState) => state.knm?.formValues?.supervisionType?.id;
export const getKnmSystemType = (state: IRootState) => state.knm?.systemType;
export const getKnmIsChangePlanReasonActive = (state: IRootState) => state.knm?.isChangePlanReasonActive;
export const getKnmIsNew = (state: IRootState) => getKnmUrlId(state) === NEW_KNM_ID;
export const getKnmEditStatus = (state: IRootState) => state.knm?.editStatus;
export const getKnmEditorTime = (state: IRootState) => state.knm?.editorTime;
export const getKnmTypesCanceled = (state: IRootState) => state.catalogs?.KNM_TYPES_CANCELED;
export const getKnmIsEditEnabledByEditors = (state: IRootState) => {
  const isNewKnm: boolean = getKnmIsNew(state) || false;

  if (isNewKnm) {
    return true;
  }
  const knmEditStatus = getKnmEditStatus(state);

  return knmEditStatus
    ? knmEditStatus === KnmEditStatusesEnum.ENABLED || knmEditStatus === KnmEditStatusesEnum.ENABLED_WAITING_OTHER
    : false;
};
export const getKnmPrevEditors = (state: IRootState) => state.knm?.prevKnmEditors;
export const getKnmTypeFormValue = (state: IRootState) => state.knm?.formValues?.type;
export const getKnmTypeErvkFormValue = (state: IRootState) => state.knm?.formValues?.typeErvk;
export const getKnmTypeIdFormValue = (state: IRootState) => state.knm?.formValues?.type?.id;
export const getKnmDownloadProsecutorDocs = (state: IRootState) => state.knm?.downloadProsecutorDocs;
export const getKnmReasonTypesSelectOptions = (state: IRootState) => {
  const getKnmReasonTypesExtendedSelectOptions = makeGetCatalogKnmConditionsSelectOptions();
  const knmTypeId = getKnmTypeIdFormValue(state);

  return getKnmReasonTypesExtendedSelectOptions(state, knmTypeId);
};

export const getKnmErpStatusesSelectOptions = (state: IRootState): IKnmStatusSelectOption[] => {
  const getKnmStatusesSelectOptions = makeGetCatalogKnmErpStatusesSelectOptions();

  return getKnmStatusesSelectOptions(state);
};
export const getKnmErpReasonTypesSelectOptions = (state: IRootState) => {
  const getKnmReasonTypesExtendedSelectOptions = makeGetCatalogKnmErpConditionsSelectOptions();
  const knmTypeCode = getKnmTypeFormValue(state)?.value ?? getKnmTypeErvkFormValue(state)?.value;
  const supervisionTypeDictVersionId = getKnmErpSupervisionTypeDictVersionIdFormValue(state);
  const knoOrganizationDictVersionId = getKnmErpKnoOrganizationDictVersionIdFormValue(state);
  const knmKindId = getKnmErvkKnmKindDictVersionIdFormValue(state);
  const isComplete336Available = getKnmIsNew(state)
    ? new Date().getFullYear() >= 2022 && new Date().getFullYear() <= 2024
    : new Date(<string>getKnmDataCreated(state)).getFullYear() >= 2022 &&
      new Date(<string>getKnmDataCreated(state)).getFullYear() <= 2024;

  return getKnmReasonTypesExtendedSelectOptions(
    state,
    knmTypeCode,
    knoOrganizationDictVersionId,
    knmKindId,
    supervisionTypeDictVersionId,
    isComplete336Available,
  );
};

export const getKnmRejectReasonTypesSelectOptions = (state: IRootState) => {
  const getKnmRejectReasonTypesExtendedSelectOptions = makeGetCatalogKnmRejectReasonTypesSelectOptions();
  const knmTypeId = getKnmTypeIdFormValue(state);

  return getKnmRejectReasonTypesExtendedSelectOptions(state, knmTypeId);
};
export const getErknmKnmRejectReasonTypesSelectOptions = (state: IRootState) => {
  const getKnmRejectReasonTypesExtendedSelectOptions = makeGetCatalogErknmKnmRejectReasonTypesSelectOptions();
  const isPlaned = getKnmTypeFormValue(state)?.value === KnmTypeCodesEnum.PP;

  return getKnmRejectReasonTypesExtendedSelectOptions(state, isPlaned);
};
export const getErknmKnmReasonTypesSelectOptions = (state: IRootState) => {
  const knoOrganizationDictVersionId = getKnmErknmKnoOrganizationDictVersionIdFormValue(state);
  const supervisionTypeDictVersionId = getKnmErknmSupervisionTypeDictVersionIdFormValue(state);
  const erknmTypeCode = getKnmTypeFormValue(state)?.value;
  const knmDataCreated = getKnmDataCreated(state);
  const erknmKind = getKnmErknmKnmKindDictVersionIdFormValue(state);

  return getCatalogErknmKnmReasonTypesSelectOptionsSelector(state)(
    knoOrganizationDictVersionId,
    supervisionTypeDictVersionId,
    erknmTypeCode,
    knmDataCreated,
    erknmKind,
  );
};

export const getPmReasonTypesSelectOptions = (state: IRootState) => {
  const getKnmReasonTypesExtendedSelectOptions = makeGetCatalogPmConditionsSelectOptions();
  const knoOrganizationDictVersionId = getKnmErknmKnoOrganizationDictVersionIdFormValue(state);
  const supervisionTypeDictVersionId = getKnmErknmSupervisionTypeDictVersionIdFormValue(state);
  const isComplete336Available = getKnmIsNew(state)
    ? new Date().getFullYear() >= 2022 && new Date().getFullYear() <= 2024
    : new Date(<string>getKnmDataCreated(state)).getFullYear() >= 2022 &&
      new Date(<string>getKnmDataCreated(state)).getFullYear() <= 2024;

  return getKnmReasonTypesExtendedSelectOptions(
    state,
    knoOrganizationDictVersionId,
    supervisionTypeDictVersionId,
    isComplete336Available,
  );
};
export const getKnmSupervisionTypesSelectOptions = (state: IRootState) => {
  const getKnmSupervisionTypesExtendedSelectOptions = makeGetCatalogKnmSupervisionTypesExtendedSelectOptions();
  const knmTypeId = getKnmTypeIdFormValue(state);
  const subjectTypeId = getKnmSubjectTypeIdFormValue(state);

  return getKnmSupervisionTypesExtendedSelectOptions(state, { knmTypeId, subjectTypeId });
};
export const getKnmErknmPositionsSelectOptions = (state: IRootState) => {
  const getDictionariesPositionsSelectOptions = makeGetDictionariesPositionsSelectOptions();
  const knoSupervisionUuid = getKnmKnoSupervisionUuid(state);

  if (knoSupervisionUuid) {
    return getDictionariesPositionsSelectOptions(state, knoSupervisionUuid);
  }

  return undefined;
};
export const getKnmErpPositionsSelectOptions = (state: IRootState) => {
  const getDictionariesPositionsSelectOptions = makeGetDictionariesPositionsSelectOptions();
  const knoSupervisionUuid = getKnmErpKnoSupervisionUuid(state);

  if (knoSupervisionUuid) {
    return getDictionariesPositionsSelectOptions(state, knoSupervisionUuid);
  }

  return undefined;
};
/**
 * Генерация списка опций для селекта разрешительных документов
 */
export const getPermitDocsSelectOptions = (state: IRootState) => {
  const getDictionariesPermitDocsSelectOptions = makeGetDictionariesPermitDocsSelectOptions();
  let controlKindUuid = getControlKindUuid(state);
  const knmSystemType = getKnmSystemType(state);

  const isErknm = knmSystemType === KnmSystemTypesEnum.ERKNM;
  const isPm = knmSystemType === KnmSystemTypesEnum.PM;

  if (isErknm || isPm) {
    if (!controlKindUuid) {
      const formRef = getKnmFormRef(state);

      controlKindUuid = formRef?.current.getFieldState(isPm ? 'supervisionTypePm' : 'kindControl')?.value?.value
        ?.dictVersionId;
    }

    if (controlKindUuid) {
      return getDictionariesPermitDocsSelectOptions(state, controlKindUuid);
    }
  }

  return undefined;
};

export const getErknmKnmRiskIndicatorsSelectOptions = (state: IRootState) => {
  const getDictionariesRiskIndicatorsSelectOptions = makeGetDictionariesRiskIndicatorsSelectOptions();

  const knoSupervisionUuid =
    getKnmSystemType(state) === KnmSystemTypesEnum.ERP
      ? getKnmErpKnoSupervisionUuid(state)
      : getKnmKnoSupervisionUuid(state);

  if (knoSupervisionUuid) {
    return getDictionariesRiskIndicatorsSelectOptions(state, knoSupervisionUuid);
  }

  return undefined;
};
export const getErknmKnmTypesSelectOptions = (state: IRootState) => {
  const knoSupervisionUuid = getKnmKnoSupervisionUuid(state);

  if (knoSupervisionUuid) {
    return getDictionariesKnmTypesData(state, knoSupervisionUuid)?.map(item => createKnmTypeSelectOption(item));
  }

  return undefined;
};
export const getKnmErknmParticipantPositionsSelectOptions = (state: IRootState) => {
  const getDictionariesParticipantPositionsSelectOptions = makeGetDictionariesParticipantPositionsSelectOptions();
  const knoSupervisionUuid = getKnmKnoSupervisionUuid(state);

  if (knoSupervisionUuid) {
    return getDictionariesParticipantPositionsSelectOptions(state, knoSupervisionUuid);
  }

  return undefined;
};
export const getKnmErpParticipantPositionsSelectOptions = (state: IRootState) => {
  const getDictionariesParticipantPositionsSelectOptions = makeGetDictionariesParticipantPositionsSelectOptions();
  const knoSupervisionUuid = getKnmErpKnoSupervisionUuid(state);

  if (knoSupervisionUuid) {
    return getDictionariesParticipantPositionsSelectOptions(state, knoSupervisionUuid);
  }

  return undefined;
};
export const getKnmErknmKindsSelectOptions = (state: IRootState) => {
  const knoOrganizationDictVersionId = getKnmErknmKnoOrganizationDictVersionIdFormValue(state);
  const supervisionTypeDictVersionId = getKnmErknmSupervisionTypeDictVersionIdFormValue(state);
  const orderVersionId = getKnmOrderVersionId(state);

  const reasonUuid =
    getKnmErknmReasonsFormValue(state)
      ?.map(x => x?.ervkUuid)
      ?.find(uuid => uuid) ||
    getKnmPmReasonsFormValue(state)
      ?.map(x => x.reason?.ervkUuid)
      ?.find(uuid => uuid) ||
    undefined;

  if (knoOrganizationDictVersionId && supervisionTypeDictVersionId) {
    const optionsWithOrderVersionId = orderVersionId
      ? getDictionariesKnmKindsSelectOptionsSelector(state)({
          knoVersionId: knoOrganizationDictVersionId,
          supervisionVersionId: supervisionTypeDictVersionId,
          conditionUuid: reasonUuid,
          orderVersionId,
        })
      : [];

    if (optionsWithOrderVersionId?.length) {
      return optionsWithOrderVersionId;
    }

    return getDictionariesKnmKindsSelectOptionsSelector(state)({
      knoVersionId: knoOrganizationDictVersionId,
      supervisionVersionId: supervisionTypeDictVersionId,
      conditionUuid: reasonUuid,
    });
  }

  return undefined;
};

export const getKnmFederalLawsSelectOptions = (state: IRootState, isNeedKnoVersionId = true, isPm?: boolean) => {
  const knoVersionId = getKnmErpKnoOrganizationDictVersionIdFormValue(state);
  const getFilteredFederalLawsSelectOptions = makeGetFilteredFederalLawsSelectOptions({ isPm });

  if (knoVersionId || !isNeedKnoVersionId) {
    return getFilteredFederalLawsSelectOptions(state);
  }

  return undefined;
};

export const getknmSubjectTypesSelectOptions = (state: IRootState) => {
  const getDictionariesTargetEntitySelectOptions = makeGetDictionariesTargetEntitySelectOptions();
  const knoOrganizationDictVersionId = getKnmErpKnoOrganizationDictVersionIdFormValue(state);
  const supervisionTypeDictVersionId = getKnmErpSupervisionTypeDictVersionIdFormValue(state);
  const reasonUuid = getKnmErpReasonErvkUuidFormValue(state);

  if (!knoOrganizationDictVersionId || !supervisionTypeDictVersionId) return undefined;

  return getDictionariesTargetEntitySelectOptions(state, {
    knoVersionId: knoOrganizationDictVersionId,
    supervisionVersionId: supervisionTypeDictVersionId,
    conditionUuid: reasonUuid,
  });
};

export const getKnmErpKindsSelectOptions = (state: IRootState) => {
  const knoOrganizationDictVersionId = getKnmErpKnoOrganizationDictVersionIdFormValue(state);
  const supervisionTypeDictVersionId = getKnmErpSupervisionTypeDictVersionIdFormValue(state);

  const reasonUuid = getKnmErpReasonErvkUuidFormValue(state);

  if (knoOrganizationDictVersionId && supervisionTypeDictVersionId) {
    return getDictionariesKnmKindsSelectOptionsSelector(state)({
      knoVersionId: knoOrganizationDictVersionId,
      supervisionVersionId: supervisionTypeDictVersionId,
      conditionUuid: reasonUuid,
    });
  }

  return undefined;
};
export const getKnmErknmProfKindsSelectOptions = (state: IRootState) => {
  const getDictionariesKnmProfKindsSelectOptions = makeGetDictionariesProfKindsSelectOptions();
  const knoOrganizationDictVersionId = getKnmErknmKnoOrganizationDictVersionIdFormValue(state);
  const supervisionTypeDictVersionId = getKnmErknmSupervisionTypeDictVersionIdFormValue(state);
  const orderVersionId = getKnmOrderVersionId(state);
  const isErknm = getCoreIsErknm(state);
  const reasonUuid = isErknm
    ? getKnmErknmReasonsFormValue(state)
        ?.map(x => x?.ervkUuid)
        ?.find(uuid => uuid) ||
      getKnmErknmReasonsFormValue(state)
        ?.map(x => x?.reason?.ervkUuid)
        ?.find(uuid => uuid) ||
      getKnmPmReasonsFormValue(state)
        ?.map(x => x.reason?.ervkUuid)
        ?.find(uuid => uuid) ||
      undefined
    : undefined;

  if (knoOrganizationDictVersionId && supervisionTypeDictVersionId && (isErknm ? reasonUuid : true)) {
    const optionsWithOrderVersionId = orderVersionId
      ? getDictionariesKnmProfKindsSelectOptions(
          state,
          {
            knoVersionId: knoOrganizationDictVersionId,
            supervisionVersionId: supervisionTypeDictVersionId,
            orderVersionId,
          },
          reasonUuid,
        )
      : [];

    if (optionsWithOrderVersionId?.length) {
      return optionsWithOrderVersionId;
    }

    return getDictionariesKnmProfKindsSelectOptions(
      state,
      {
        knoVersionId: knoOrganizationDictVersionId,
        supervisionVersionId: supervisionTypeDictVersionId,
      },
      reasonUuid,
    );
  }

  return undefined;
};

export const getKnmErpRiskCategoriesSelectOptions = (state: IRootState) => {
  const knoSupervisionUuid = getKnmErpKnoSupervisionUuid(state);
  const isPlanned = getKnmIsTypeErpPlanned(state);
  const orderVersionId = getKnmOrderVersionId(state);
  const reasonUuid =
    getKnmErpReasonsErvkFormValue(state)
      ?.map(x => x.type?.ervkUuid)
      .find(uuid => uuid) || undefined;

  if (knoSupervisionUuid) {
    const optionsWithOrderVersionId = orderVersionId
      ? makeGetDictionariesRiskCategoriesSelectOptions(
          state,
          knoSupervisionUuid,
          reasonUuid,
          orderVersionId,
          undefined,
          isPlanned,
        )
      : [];

    if (optionsWithOrderVersionId?.length) {
      return optionsWithOrderVersionId;
    }

    return makeGetDictionariesRiskCategoriesSelectOptions(
      state,
      knoSupervisionUuid,
      reasonUuid,
      undefined,
      undefined,
      isPlanned,
    );
  }

  return undefined;
};
export const getKnmErknmRiskCategoriesSelectOptions = (state: IRootState) => {
  const knmSystemType = getKnmSystemType(state);
  const knoSupervisionUuid = getKnmKnoSupervisionUuid(state);
  const isPP = getKnmIsTypeErknmPlanned(state);
  const orderVersionId = getKnmOrderVersionId(state);
  const knmKindUuid = getKnmErknmKnmKindDictVersionIdFormValue(state);
  const reasonUuid =
    getKnmErknmReasonsFormValue(state)
      ?.map(x => x?.ervkUuid)
      ?.find(uuid => uuid) ||
    getKnmPmReasonsFormValue(state)
      ?.map(x => x.reason?.ervkUuid)
      ?.find(uuid => uuid) ||
    undefined;

  if (knoSupervisionUuid) {
    const optionsWithOrderVersionId = orderVersionId
      ? makeGetDictionariesRiskCategoriesSelectOptions(
          state,
          knoSupervisionUuid,
          reasonUuid,
          orderVersionId,
          knmSystemType === KnmSystemTypesEnum.ERKNM ? knmKindUuid : undefined,
          isPP,
        )
      : [];

    if (optionsWithOrderVersionId?.length) {
      return optionsWithOrderVersionId;
    }

    return makeGetDictionariesRiskCategoriesSelectOptions(
      state,
      knoSupervisionUuid,
      reasonUuid,
      undefined,
      knmSystemType === KnmSystemTypesEnum.ERKNM ? knmKindUuid : undefined,
      isPP,
    );
  }

  return undefined;
};
export const getKnmErknmDangerClassesSelectOptions = (state: IRootState) => {
  const knoSupervisionUuid = getKnmKnoSupervisionUuid(state);
  const orderVersionId = getKnmOrderVersionId(state);
  const reasonUuid =
    getKnmErknmReasonsFormValue(state)
      ?.map(x => x?.ervkUuid)
      ?.find(uuid => uuid) ||
    getKnmPmReasonsFormValue(state)
      ?.map(x => x.reason?.ervkUuid)
      ?.find(uuid => uuid) ||
    undefined;
  const erknmTypeCode = getKnmTypeFormValue(state)?.value;

  if (knoSupervisionUuid) {
    const optionsWithOrderVersionId = orderVersionId
      ? makeGetDictionariesDangerClassesSelectOptions(
          state,
          knoSupervisionUuid,
          reasonUuid,
          orderVersionId,
          erknmTypeCode === KnmTypeCodesEnum.PP,
        )
      : [];

    if (optionsWithOrderVersionId?.length) {
      return optionsWithOrderVersionId;
    }

    return makeGetDictionariesDangerClassesSelectOptions(
      state,
      knoSupervisionUuid,
      reasonUuid,
      undefined,
      erknmTypeCode === KnmTypeCodesEnum.PP,
    );
  }

  return undefined;
};
export const getKnmErpDangerClassesSelectOptions = (state: IRootState) => {
  const knoSupervisionUuid = getKnmErpKnoSupervisionUuid(state);
  const isPlanned = getKnmIsTypeErpPlanned(state);
  const orderVersionId = getKnmOrderVersionId(state);
  const reasonUuid =
    getKnmErpReasonsErvkFormValue(state)
      ?.map(x => x.type?.ervkUuid)
      .find(uuid => uuid) || undefined;

  if (knoSupervisionUuid) {
    const optionsWithOrderVersionId = orderVersionId
      ? makeGetDictionariesDangerClassesSelectOptions(state, knoSupervisionUuid, reasonUuid, orderVersionId, isPlanned)
      : [];

    if (optionsWithOrderVersionId?.length) {
      return optionsWithOrderVersionId;
    }

    return makeGetDictionariesDangerClassesSelectOptions(state, knoSupervisionUuid, reasonUuid, undefined, isPlanned);
  }

  return undefined;
};
export const getKnmErknmSubjectTypesSelectOptions = (state: IRootState) => {
  const getDictionariesSubjectTypesSelectOptions = makeGetDictionariesSubjectTypesSelectOptions();
  const knoSupervisionUuid = getKnmKnoSupervisionUuid(state);
  const orderVersionId = getKnmOrderVersionId(state);

  const reasonUuid =
    getKnmErknmReasonsFormValue(state)
      ?.map(x => x?.ervkUuid)
      ?.find(uuid => uuid) ||
    getKnmPmReasonsFormValue(state)
      ?.map(x => x.reason?.ervkUuid)
      ?.find(uuid => uuid) ||
    undefined;

  if (knoSupervisionUuid) {
    const optionsWithOrderVersionId = orderVersionId
      ? getDictionariesSubjectTypesSelectOptions(state, knoSupervisionUuid, reasonUuid, orderVersionId)
      : [];

    if (optionsWithOrderVersionId?.length) {
      return optionsWithOrderVersionId;
    }

    return getDictionariesSubjectTypesSelectOptions(state, knoSupervisionUuid, reasonUuid);
  }

  return undefined;
};

export const getKnmErpSubjectTypesSelectOptions = (state: IRootState) => {
  const getDictionariesSubjectTypesSelectOptions = makeGetDictionariesSubjectTypesSelectOptions();
  const knoSupervisionUuid = getKnmErpKnoSupervisionUuid(state);

  if (knoSupervisionUuid) {
    return getDictionariesSubjectTypesSelectOptions(state, knoSupervisionUuid);
  }

  return undefined;
};

export const getKnmErknmSubjectKindsSelectOptions = (state: IRootState, objectTypeDictVersionId?: string) => {
  const getDictionariesSubjectKindsSelectOptions = makeGetDictionariesSubjectKindsSelectOptions();
  const knoSupervisionUuid = getKnmKnoSupervisionUuid(state);
  const orderVersionId = getKnmOrderVersionId(state);
  const reasonUuid =
    getKnmErknmReasonsFormValue(state)
      ?.map(x => x?.ervkUuid)
      ?.find(uuid => uuid) ||
    getKnmPmReasonsFormValue(state)
      ?.map(x => x.reason?.ervkUuid)
      ?.find(uuid => uuid) ||
    undefined;

  if (knoSupervisionUuid) {
    const optionsWithOrderVersionId = orderVersionId
      ? getDictionariesSubjectKindsSelectOptions(
          state,
          knoSupervisionUuid,
          reasonUuid,
          orderVersionId,
          objectTypeDictVersionId,
        )
      : [];

    if (optionsWithOrderVersionId?.length) {
      return optionsWithOrderVersionId;
    }

    return getDictionariesSubjectKindsSelectOptions(
      state,
      knoSupervisionUuid,
      reasonUuid,
      undefined,
      objectTypeDictVersionId,
    );
  }

  return undefined;
};
export const getKnmErknmSubjectSubKindsSelectOptions = (state: IRootState, objectTypeDictVersionId?: string) => {
  const getDictionariesSubjectSubKindsSelectOptions = makeGetDictionariesSubjectSubKindsSelectOptions();
  const knoSupervisionUuid = getKnmKnoSupervisionUuid(state);
  const orderVersionId = getKnmOrderVersionId(state);
  const reasonUuid =
    getKnmErknmReasonsFormValue(state)
      ?.map(x => x?.ervkUuid)
      ?.find(uuid => uuid) ||
    getKnmPmReasonsFormValue(state)
      ?.map(x => x.reason?.ervkUuid)
      ?.find(uuid => uuid) ||
    undefined;

  if (knoSupervisionUuid) {
    const optionsWithOrderVersionId = orderVersionId
      ? getDictionariesSubjectSubKindsSelectOptions(
          state,
          knoSupervisionUuid,
          reasonUuid,
          orderVersionId,
          objectTypeDictVersionId,
        )
      : [];

    if (optionsWithOrderVersionId?.length) {
      return optionsWithOrderVersionId;
    }

    return getDictionariesSubjectSubKindsSelectOptions(
      state,
      knoSupervisionUuid,
      reasonUuid,
      undefined,
      objectTypeDictVersionId,
    );
  }

  return undefined;
};
export const getKnmEventTypesSelectOptions = (state: IRootState) => {
  const getDictionariesEventTypesSelectOptions = makeGetDictionariesEventTypesSelectOptions();
  const orderVersionId = getKnmOrderVersionId(state);
  const reasonUuid =
    getKnmErknmReasonsFormValue(state)
      ?.map(x => x?.ervkUuid)
      ?.find(uuid => uuid) ||
    getKnmPmReasonsFormValue(state)
      ?.map(x => x.reason?.ervkUuid)
      ?.find(uuid => uuid) ||
    undefined;

  const uniqueUuid =
    getKnmSystemType(state) === KnmSystemTypesEnum.ERKNM
      ? getKnmEventUuid(state)?.find(x => x)
      : getKnmKnoSupervisionUuid(state);

  if (uniqueUuid) {
    const optionsWithOrderVersionId = orderVersionId
      ? getDictionariesEventTypesSelectOptions(state, uniqueUuid, reasonUuid, orderVersionId)
      : [];

    if (optionsWithOrderVersionId?.length) {
      return optionsWithOrderVersionId;
    }

    return getDictionariesEventTypesSelectOptions(state, uniqueUuid, reasonUuid);
  }

  return undefined;
};

export const getKnmErknmSupervisionTypesSelectOptions = (state: IRootState) => {
  const knoOrganizationDictVersionId = getKnmErknmKnoOrganizationDictVersionIdFormValue(state);
  const isPlanned = getKnmIsTypeErknmPlanned(state) || !!getKnmDataPlanId(state);

  const settings = getAdminFetchSettings(state);

  const isUseTransferringAuthorities = isLaterThanAdminSetting(
    getKnmDataCreated(state),
    settings,
    AdminSettingCodesEnum.TRANSFERRING_AUTHORITIES_4_1_22_STARTS_FROM,
  );

  return getDictionariesSupervisionTypesSelectOptionsSelector(state)(
    knoOrganizationDictVersionId,
    isUseTransferringAuthorities,
    [FEDERAL_LOW_248_GUID],
    isPlanned,
  );
};

export const getKnmErpSupervisionTypesSelectOptions = (state: IRootState) => {
  const knoOrganizationDictVersionId = getKnmErpKnoOrganizationDictVersionIdFormValue(state);
  const federalLawDictVersionId = getKnmErpFederalLawDictVersionIdFormValue(state);
  const isPlanned = getKnmIsTypeErpPlanned(state) || !!getKnmDataPlanId(state);

  const settings = getAdminFetchSettings(state);
  const isUseTransferringAuthorities = isLaterThanAdminSetting(
    getKnmDataCreated(state),
    settings,
    AdminSettingCodesEnum.TRANSFERRING_AUTHORITIES_4_1_22_STARTS_FROM,
  );

  if (federalLawDictVersionId) {
    return getDictionariesSupervisionTypesSelectOptionsSelector(state)(
      knoOrganizationDictVersionId,
      isUseTransferringAuthorities,
      [federalLawDictVersionId],
      isPlanned,
    );
  }

  return undefined;
};

export const getKnmErknmAcceptedDocumentsSelectOptions = (state: IRootState) => {
  const getDictionariesAcceptedDocumentsSelectOptions = makeGetDictionariesAcceptedDocumentsSelectOptions();
  const knoOrganizationDictVersionId = getKnmErknmKnoOrganizationDictVersionIdFormValue(state);
  const supervisionTypeDictVersionId = getKnmErknmSupervisionTypeDictVersionIdFormValue(state);

  if (knoOrganizationDictVersionId && supervisionTypeDictVersionId) {
    return getDictionariesAcceptedDocumentsSelectOptions(state, {
      knoVersionId: knoOrganizationDictVersionId,
      supervisionVersionId: supervisionTypeDictVersionId,
    });
  }

  return undefined;
};
export const getKnmErknmChecklistsSelectOptions = (state: IRootState) => {
  const getDictionariesChecklistsSelectOptions = makeGetDictionariesChecklistsSelectOptions();
  const knoOrganizationDictVersionId = getKnmErknmKnoOrganizationDictVersionIdFormValue(state);
  const supervisionTypeDictVersionId = getKnmErknmSupervisionTypeDictVersionIdFormValue(state);

  if (knoOrganizationDictVersionId && supervisionTypeDictVersionId) {
    return getDictionariesChecklistsSelectOptions(state, {
      uuidKno: knoOrganizationDictVersionId,
      uuidSupervision: supervisionTypeDictVersionId,
    });
  }

  return undefined;
};
export const getKnmErknmErvkChecklistsSelectOptions = (state: IRootState) => {
  const getErvkChecklistsSelectOptions = makeGetErvkChecklistsSelectOptions();
  const supervisionTypeDictVersionId = getKnmErknmSupervisionTypeDictVersionIdFormValue(state);
  const knmKindDictVersionId = getKnmErknmKnmKindDictVersionIdFormValue(state);

  if (knmKindDictVersionId && supervisionTypeDictVersionId) {
    return getErvkChecklistsSelectOptions(state, {
      controlKindId: supervisionTypeDictVersionId,
      knmKindId: knmKindDictVersionId,
    });
  }

  return undefined;
};
export const getKnmErpErvkChecklistsSelectOptions = (state: IRootState) => {
  const getErvkChecklistsSelectOptions = makeGetErvkChecklistsSelectOptions();
  const supervisionTypeDictVersionId = getKnmErvkSupervisionTypeDictVersionIdFormValue(state);
  const knmKindDictVersionId = getKnmErvkKnmKindDictVersionIdFormValue(state);

  if (knmKindDictVersionId && supervisionTypeDictVersionId) {
    return getErvkChecklistsSelectOptions(state, {
      controlKindId: supervisionTypeDictVersionId,
      knmKindId: knmKindDictVersionId,
    });
  }

  return undefined;
};
export const getKnmErknmRequirementsSelectOptions = (state: IRootState) => {
  const getDictionariesRequirementsSelectOptions = makeGetDictionariesRequirementsSelectOptions();
  const knoOrganizationDictVersionId = getKnmErknmKnoOrganizationDictVersionIdFormValue(state);
  const supervisionTypeDictVersionId = getKnmErknmSupervisionTypeDictVersionIdFormValue(state);

  if (knoOrganizationDictVersionId && supervisionTypeDictVersionId) {
    return getDictionariesRequirementsSelectOptions(state, {
      uuidKno: knoOrganizationDictVersionId,
      uuidSupervision: supervisionTypeDictVersionId,
    });
  }

  return undefined;
};
const getRegistryExportOkvedItemSelectOptions = makeGetRegistryExportOkvedItemSelectOptions();

export const getKnmOkvedsSelectOptions = (state: IRootState) => {
  const data = getKnmSubjectData(state);

  if (data?.inn && data?.ogrn) {
    const { inn, ogrn } = data;

    return getRegistryExportOkvedItemSelectOptions(state, { inn, ogrn });
  }

  return undefined;
};

export const getKnmOkvedsSelectOptionsByParams = (params: Partial<IRegistryExportOkvedFilterParams> | undefined) => (
  state: IRootState,
) => {
  if (params?.inn && params?.ogrn) {
    const { inn, ogrn } = params;

    return getRegistryExportOkvedItemSelectOptions(state, { inn, ogrn });
  }

  return undefined;
};

const getAccountChecklistTemplatesItemSelectOptions = makeGetAccountChecklistTemplatesItemSelectOptions();

export const getKnmChecklistTemplatesSelectOptions = (state: IRootState) => {
  return getAccountChecklistTemplatesItemSelectOptions(state, knmChecklistTemplatesGetBasicParams);
};
export const getKnmAvailableChecklistTemplatesSelectOptions = createSelector(
  [getKnmChecklistTemplatesSelectOptions, getKnmKnoOrganizationIdFormValue, getKnmErpOrganizationCodeFormValue],
  (knmChecklistTemplatesSelectOptions, formKnoOrganizationId, formErpOrganizationCode) => {
    const knoOrganizationId = formKnoOrganizationId || formErpOrganizationCode || undefined;

    return knoOrganizationId
      ? knmChecklistTemplatesSelectOptions?.filter(item => item.knoOrganizationId === knoOrganizationId)
      : undefined;
  },
);
const getAccountRequirementTemplatesItemSelectOptions = makeGetAccountRequirementTemplatesItemSelectOptions();

export const getKnmRequirementTemplatesSelectOptions = (state: IRootState) => {
  return getAccountRequirementTemplatesItemSelectOptions(state, knmRequirementTemplatesGetBasicParams);
};
export const getKnmAvailableRequirementTemplatesSelectOptions = createSelector(
  [getKnmRequirementTemplatesSelectOptions, getKnmKnoOrganizationIdFormValue, getKnmSupervisionTypeIdFormValue],
  (knmRequirementTemplatesSelectOptions, formKnoOrganizationId, formSupervisionTypeId) => {
    if (formKnoOrganizationId && formSupervisionTypeId) {
      return knmRequirementTemplatesSelectOptions?.filter(
        item => item.knoOrganizationId === formKnoOrganizationId && item.supervisionType === formSupervisionTypeId,
      );
    }

    return knmRequirementTemplatesSelectOptions;
  },
);

const getAccountKnoOrganizationFunctionsGroupSelectOptions = makeGetAccountKnoOrganizationFunctionsGroupSelectOptions();

export const getKnmKnoOrganizationFunctionsSelectOptions = (state: IRootState) => {
  const formKnoOrganizationId = getKnmKnoOrganizationIdFormValue(state);

  if (formKnoOrganizationId) {
    return getAccountKnoOrganizationFunctionsGroupSelectOptions(state, formKnoOrganizationId);
  }

  return undefined;
};

export const getKnmHistoryChangesIsOn = (state: IRootState) => !!getKnm(state)?.knmHistoryChangesIsOn;
export const getKnmHistoryChanges = (state: IRootState) => getKnm(state)?.knmHistoryChanges;
export const getKnmHistoryChangesIsLoading = (state: IRootState) => !!getKnm(state)?.knmHistoryChangesIsLoading;

export const getKnmSignedHistoryChanges = (state: IRootState) => getKnm(state)?.knmSignedHistoryChanges;

export const getKnmSubjectTypeCode = (state: IRootState) => getKnmData(state)?.organization?.type?.code;
export const getKnmIsInitialData = (state: IRootState) => getKnmData(state)?.initial;
export const getKnmIsSeveralOrganizations = (state: IRootState) => getKnmData(state)?.isSeveralOrganizations;
export const getKnmProsecutorOrganizationId = (state: IRootState) => getKnmData(state)?.prosecutorOrganization?.id;
export const getKnmSubjectData = (state: IRootState) => state.knm?.subjectData;
export const getKnmCollaboratingOrganizationsErknm = (state: IRootState) =>
  getKnmData(state)?.knmErknm?.collaboratingOrganizations;

export const getKnmExcludeDates = (state: IRootState) => state?.knm?.excludeDates;

export const getKnmWorkDaysCountBeforeStart = (state: IRootState) => state?.knm?.workDaysCountBeforeStart;

export const getKnmPlannedActivities = (state: IRootState) => {
  return state?.knm?.plannedActivities;
};
export const getNKOForeignAgentData = (state: IRootState) => state?.knm?.nkoForeignAgentData;

export const getCreatedJudgmentCourt = (state: IRootState) => state?.knm?.createdCourt;

export const getTargetEntitySelectOptions = (state: IRootState) => {
  const getDictionariesTargetEntitySelectOptions = makeGetDictionariesTargetEntitySelectOptions();
  const knoOrganizationDictVersionId = getKnmErknmKnoOrganizationDictVersionIdFormValue(state);
  const supervisionTypeDictVersionId = getKnmErknmSupervisionTypeDictVersionIdFormValue(state);
  const orderVersionId = getKnmOrderVersionId(state);

  const reasonUuid =
    getKnmErknmReasonsFormValue(state)
      ?.map(x => x?.ervkUuid)
      ?.find(uuid => uuid) ||
    getKnmPmReasonsFormValue(state)
      ?.map(x => x.reason?.ervkUuid)
      ?.find(uuid => uuid) ||
    undefined;

  if (knoOrganizationDictVersionId && supervisionTypeDictVersionId) {
    const optionsWithOrderVersionId = orderVersionId
      ? getDictionariesTargetEntitySelectOptions(state, {
          knoVersionId: knoOrganizationDictVersionId,
          supervisionVersionId: supervisionTypeDictVersionId,
          conditionUuid: reasonUuid,
          orderVersionId,
        })
      : [];

    if (optionsWithOrderVersionId?.length) {
      return optionsWithOrderVersionId;
    }

    return getDictionariesTargetEntitySelectOptions(state, {
      knoVersionId: knoOrganizationDictVersionId,
      supervisionVersionId: supervisionTypeDictVersionId,
      conditionUuid: reasonUuid,
    });
  }

  return undefined;
};

export const getTargetEntityFounderSelectOptions = (state: IRootState) => {
  const getDictionariesTargetEntityFounderSelectOptions = makeGetDictionariesTargetEntityFounderSelectOptions();

  return getDictionariesTargetEntityFounderSelectOptions(state);
};

export const getKnmIsTypeErknmPlanned = (state: IRootState) =>
  getKnmFormRef(state)?.current.getFieldState('typeErknm')?.value?.code === KnmTypeCodesEnum.PP;

export const getKnmIsTypeErpPlanned = (state: IRootState) =>
  getKnmTypeFormValue(state)?.code === KnmTypeCodesEnum.PP ||
  getKnmTypeErvkFormValue(state)?.code === KnmTypeCodesEnum.PP;

export const getKnmIsEditableInStatusCommentsExist = (state: IRootState) => {
  const knmPlanStatus = getPlanListPlanErknmStatus(state);
  const userDate = spacetime.now(tzMoscow);
  const planStartDate = getPlanListPlanStartDate(state);
  const planStartYear = parseDate({ date: planStartDate || '', parseFormat: SYSTEM_DATE_FORMAT_FNS })?.getFullYear();
  const remarks = getKnmDataErknmApproveRemarks(state);

  return (
    getUserRoleIsSupervisor(state) &&
    getKnmIsErknmStatusCommentsExist(state) &&
    !remarks?.length &&
    getIsPlanErknmStatusConsidered(knmPlanStatus) &&
    !!planStartYear &&
    planStartYear > userDate.year()
  );
};

export const getKnmStartDateIsCurrentYear = (state: IRootState) => {
  const userDate = spacetime.now(tzMoscow);
  const stringStartDate = getKnmFormRef(state)?.current.getFieldState('startDate')?.value;
  const date = parseDate({ date: stringStartDate || '', parseFormat: DATE_FORMAT_LONG_FNS });

  return userDate.year() === date?.getFullYear();
};

// Набор настроек, который разрешает кнопку "Перезапросить" внутри КНМ/ПМ -> Сведения об объектах контроля
export const getKnmIsCanRefreshControlObject = (state: IRootState) => {
  const startDate = getKnmDataStartDate(state);
  const userDate = spacetime.now(tzMoscow);
  const startDateSpacetime = spacetime(startDate, tzMoscow);

  // Кнопка обновления доступна, если статус
  return (
    // В процессе заполнения
    getKnmIsErknmStatusFillInProcess(state) ||
    // Готово к согласованию
    getKnmIsErknmStatusReadyForApprove(state) ||
    // На согласовании
    getKnmIsErknmStatusApproving(state) ||
    // Ожидает проведения, но дата начала не еще наступила
    (getKnmIsErknmStatusTypeWaitingCarryOut(state) && userDate.isBefore(startDateSpacetime))
  );
};

export const isKnmStartDateBeforeUserDate = (state: IRootState) => {
  const startDate = getKnmDataStartDate(state);
  const userDate = spacetime.now(tzMoscow);
  const startDateSpacetime = spacetime(startDate, tzMoscow);

  return userDate.isBefore(startDateSpacetime);
};

export const getSonkoRegistryData = (state: IRootState) => state?.knm?.isOrgInSonkoRegistry;

export const getKnmAccessSettingsFormRef = (state: IRootState) => state?.knm?.accessSettingsFormRef;
export const getKnmInitialAccessSettingsFormValues = (state: IRootState) => state?.knm?.initialAccessSettingsFormValues;
export const getKnmAccessNoticeErpId = (state: IRootState) => state?.knm?.accessNoticeErpId;
export const getKnmAccessNoticeKnoOrganizationName = (state: IRootState) => state?.knm?.accessNoticeKnoOrganizationName;

export const getEpguNotificationList = (state: IRootState) => state?.knm?.epguNotificationList;
export const getEpguNotificationTotalCount = (state: IRootState) => state?.knm?.epguNotificationTotalCount;

export const getApproveAddNotificationFunction = (state: IRootState) => state?.knm?.approveAddNotificationFunction;

export const getErvkObjectsOfControl = (state: IRootState) => state?.knm?.ervkObjectsOfControl;
export const getKnmErknmKnmKindDictionaryValue = (state: IRootState) => {
  const knmKind = getKnmErknmKnmKindDictVersionIdFormValue(state);

  if (knmKind) {
    return getDictionariesItemValueData(state, DictionaryUUIDsEnum.KNM_KIND, knmKind);
  }

  return undefined;
};
export const getKnmErknmReasonsFormValue = (state: IRootState) => state.knm?.formValues?.reasonsErknm;

export const getKnmErpReasonsFormValue = (state: IRootState) => state.knm?.formValues?.reasons;
export const getKnmErpReasonsErvkFormValue = (state: IRootState) => state.knm?.formValues?.reasonsErvk;
// Порядок как в KnmErpContent при загрузке справочников: сначала reasonsErvk, потом старые reasons.
export const getKnmErpReasonErvkUuidFormValue = (state: IRootState) =>
  [...(getKnmErpReasonsErvkFormValue(state) ?? []), ...(getKnmErpReasonsFormValue(state) ?? [])]
    .map(x => x?.type?.ervkUuid)
    .find(uuid => uuid) || undefined;

export const getKnmPmIsUnableToRefuseFormValue = (state: IRootState) => state.knm?.formValues?.isUnableToRefuse;

export const getKnmPmReasonsFormValue = (state: IRootState) => state.knmPm?.initialFormValues?.reasonsPm;

export const getKnmOrderVersionId = (state: IRootState) => state.knm.orderVersionId;

export const getKnmOrdersSelectOptions = (state: IRootState) => state.knm.knmOrdersSelectOptions;
export const getKnmPlanExclusionReasonsSelectOptions = (state: IRootState) =>
  state.knm.planExclusionReasonsSelectOptions;
export const getKnmOrdersSelectConditionId = (state: IRootState) => state.knm.knmOrderConditionId;

export const getCatalogKnmTypes248SelectOptions = (state: IRootState) => {
  const knoOrganizationDictVersionId = getKnmErknmKnoOrganizationDictVersionIdFormValue(state);
  const supervisionTypeDictVersionId = getKnmErknmSupervisionTypeDictVersionIdFormValue(state);

  return getCatalogKnmTypes248SelectOptionsSelector(state)({
    knoVersionId: knoOrganizationDictVersionId,
    supervisionVersionId: supervisionTypeDictVersionId,
  });
};

export const getCatalogKnmTypesErpSelectOptions = (state: IRootState) => {
  const knoOrganizationDictVersionId = getKnmErpKnoOrganizationDictVersionIdFormValue(state);
  const supervisionTypeDictVersionId = getKnmErpSupervisionTypeDictVersionIdFormValue(state);

  if (knoOrganizationDictVersionId && supervisionTypeDictVersionId) {
    return getCatalogKnmTypes294SelectOptions(state);
  }

  return undefined;
};
export const getChangePlanReasonAfterConfirmEvent = (state: IRootState) => state.knm?.changePlanReasonAfterConfirm;

export const getIsKnmCanBeSigned = (state: IRootState) => state.knm.isKnmCanBeSigned;
export const getErvkCorrectionRequired = (state: IRootState) => state.knm.ervkCorrectionRequired;

export const isStateForCheckErvkCorrectionRequired = (state: IRootState) => {
  const isSupervisor: boolean | undefined = getUserRoleIsSupervisor(state);
  const isPlanned = getKnmIsTypeErknmPlanned(state);
  const isKnmStatusTypeWaitingCarryOut = getKnmIsErknmStatusTypeWaitingCarryOut(state);

  return isSupervisor && isPlanned && isKnmStatusTypeWaitingCarryOut;
};

export const getIsEventErvkCorrectionRequired = (state: IRootState) => {
  const ervkCorrectionRequired = getErvkCorrectionRequired(state);

  return (
    isStateForCheckErvkCorrectionRequired(state) &&
    (ervkCorrectionRequired ? ervkCorrectionRequired.needCorrectionEvent : false)
  );
};

export const getIsControlObjectsErvkCorrectionRequired = (state: IRootState) => {
  const ervkCorrectionRequired = getErvkCorrectionRequired(state);

  return (
    isStateForCheckErvkCorrectionRequired(state) &&
    (ervkCorrectionRequired ? ervkCorrectionRequired.needCorrectionControlObjects : false)
  );
};

export const getIsParticipantPositionErvkCorrectionRequired = (state: IRootState) => {
  const ervkCorrectionRequired = getErvkCorrectionRequired(state);

  return (
    isStateForCheckErvkCorrectionRequired(state) &&
    (ervkCorrectionRequired ? ervkCorrectionRequired.needCorrectionParticipantPosition : false)
  );
};

export const getKnmInvalidFields = (state: IRootState) => state.knm.invalidFields;

export const getErvkProsecutorOrganization = (state: IRootState) => state.knm.ervkProsecutorOrganization?.data;

export const getProsecutorOrganizationsWithDistrictSelectOptions = createSelector(
  getErvkProsecutorOrganization,
  ervkProsecutorOrganization =>
    ervkProsecutorOrganization ? createUserOrganizationWithDistrictSelectOption(ervkProsecutorOrganization) : undefined,
);

export const getKnmCollaboratingKnoOrganisations = (state: IRootState) =>
  state.knm.collaboratingKnoOrganizations.knoOrganisations;
export const getKnmCollaboratingOrganisationsPageCount = (state: IRootState) =>
  state.knm.collaboratingKnoOrganizations.organisationsPageCount;
export const getKnmCollaboratingOrganisationsPage = (state: IRootState) =>
  state.knm.collaboratingKnoOrganizations.organisationsPage;
export const getKnmCollaboratingOrganisationsTotalCount = (state: IRootState) =>
  state.knm.collaboratingKnoOrganizations.organisationsTotalCount;

export const getKnmIsShowApproveRemarks = (state: IRootState) => state.knm.isShowApproveRemarks;

export const getKnmIsNeedActSaveOrSign = (state: IRootState) => state.knm.isNeedActSaveOrSign;

export const getErknmKnmPrescribeDataForCancelSign = (state: IRootState) => state.knm.prescribeDataForSign;
export const getErknmKnmPrescribeIsCancelSignCompleted = (state: IRootState) => state.knm.isCancelSignCompleted;

export const getKnmExternalParams = (state: IRootState) => state.knm.externalParams;

export const getKnmIsSaveOnProcess = (state: IRootState) => state.knm.isSaveOnProcess;

export const getKnmOldKnoOrganization = (state: IRootState) => getKnmData(state)?.oldKnoOrganization;
export const getKnmDateReasonChangeKno = (state: IRootState) => getKnmData(state)?.dateReasonChangeKno;
export const getKnmOldNameControllingSubject = (state: IRootState) => getKnmData(state)?.oldNameControllingSubject;
export const getKnmDateChangeControllingSubject = (state: IRootState) =>
  getKnmData(state)?.dateReasonChangeControllingSubject;
